import { useFormikContext } from 'formik'
import { useCallback } from 'react'

import type { IQuestionnaireQuestion } from '@healthblocks-io/core'

/**
 * Returns a wrapped `onNext` for Array-type questions that strips any unsaved
 * empty entries before navigating.
 *
 * When the user clicks "ga verder" while an array entry form is open but not
 * yet saved, the entry has already been pushed to Formik with all fields set
 * to empty strings. Removing it here keeps the answer clean and prevents the
 * backend from receiving malformed data.
 */
export const useArrayFieldNext = (data: IQuestionnaireQuestion, questionId: string, onNext: () => void) => {
  const { values, setFieldValue } = useFormikContext()

  return useCallback(() => {
    if (data.type === 'Array' && data.fields) {
      const arrayValue = (values as Record<string, unknown>)[questionId]

      if (Array.isArray(arrayValue)) {
        const cleaned = arrayValue.filter(
          (entry: unknown) =>
            typeof entry === 'object' &&
            entry !== null &&
            Object.values(entry as Record<string, unknown>).some((v) => v !== ''),
        )

        if (cleaned.length !== arrayValue.length) {
          setFieldValue(questionId, cleaned)
        }
      }
    }

    onNext()
  }, [data.type, data.fields, values, questionId, setFieldValue, onNext])
}
